import { useBookDetailsQuery } from '@/common/apis/services/booking/getBookDetails';
import { useRemoveBook } from '@/common/apis/services/booking/removeBook';
import { useCenterPayment } from '@/common/apis/services/factor/centerPayment';
import { useConsultPayment } from '@/common/apis/services/factor/consultPayment';
import Button from '@/common/components/atom/button';
import Modal from '@/common/components/atom/modal';
import Text from '@/common/components/atom/text';
import { splunkInstance } from '@/common/services/splunk';
import { CENTERS } from '@/common/types/centers';
import { picUserImageUrl } from '@/common/utils/picUserImageUrl';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { resolveRetryPaymentMethod } from '@/modules/booking/utils/retryPaymentMethod';
import { CANCEL_REASONS, CancelReason, LeavingNotice, PendingPaymentFollowUp } from './pendingPaymentFollowUp';
import classNames from '@/common/utils/classNames';
import { getImageUrl } from '@/common/utils/getImageUrl';
import getConfig from 'next/config';
import getDisplayDoctorExpertise from '@/common/utils/getDisplayDoctorExpertise';
import { useFactorDoctorData } from '@/modules/booking/hooks/factor/useFactorDoctorData';
import Avatar from '@/common/components/atom/avatar';
import Skeleton from '@/common/components/atom/skeleton';
import ChatIcon from '@/common/components/icons/chat';
import ChevronIcon from '@/common/components/icons/chevron';
import CalenderIcon from '@/common/components/icons/calender';
import DoctorIcon from '@/common/components/icons/doctor';
import EditIcon from '@/common/components/icons/edit';
import MoneyIcon from '@/common/components/icons/money';
import OfficeIcon from '@/common/components/icons/office';
import RefreshIcon from '@/common/components/icons/refresh';
import ErrorIcon from '@/common/components/icons/error';
import SecuritySafeIcon from '@/common/components/icons/securitySafe';
import { applyOnlineVisitPatientDisplayBufferMs } from '@/modules/booking/utils/onlineVisitPatientDisplayTime';
import {
  clearPendingPayment,
  PAID_BOOKS_STORAGE_KEY,
  PENDING_PAYMENT_TTL_MS,
  PendingPayment,
  readPendingPayment,
  shouldOpenPendingPaymentSheet,
} from '@/modules/booking/utils/pendingPayment';
import { digitsFaToEn } from '@persian-tools/persian-tools';
import moment from 'jalali-moment';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { setRumBook } from '@/common/services/rum/elastic/ids';

const { publicRuntimeConfig } = getConfig();

const REASON_ICONS: Record<CancelReason, React.ComponentType<{ className?: string }>> = {
  inPerson: OfficeIcon,
  changeTime: RefreshIcon,
  price: MoneyIcon,
  slow: CalenderIcon,
  otherDoctor: DoctorIcon,
  bank: SecuritySafeIcon,
  other: EditIcon,
};

/** Tells an open factor page that the held book was paid for / cancelled from the sheet. */
export const PENDING_PAYMENT_CHANGED_EVENT = 'pending-payment-changed';

const formatBookTime = (time?: string) => {
  if (!time) return undefined;
  const parsed = moment.from(digitsFaToEn(time), 'fa', 'JYYYY/JMM/JDD HH:mm');
  if (!parsed?.isValid()) return time;
  return moment(applyOnlineVisitPatientDisplayBufferMs(parsed.valueOf()))
    .locale('fa')
    .calendar(undefined, {
      nextWeek: 'YYYY/MM/DD(dddd) حدود ساعت HH:mm',
      sameElse: 'YYYY/MM/DD حدود ساعت HH:mm',
      sameDay: 'امروز حدود ساعت HH:mm',
      nextDay: 'فردا حدود ساعت HH:mm',
    });
};

const track = (type: string, pending: PendingPayment, extra: Record<string, unknown> = {}) => {
  try {
    splunkInstance('booking').sendEvent({
      group: 'factor_payment_retry',
      type,
      event: { book_id: pending.bookId, center_id: pending.centerId, payment_method: pending.paymentMethod, path: window.location.pathname, ...extra },
    });
  } catch {
    // analytics must never block payment
  }
};

/**
 * Back from the bank without paying, on any page: show the held book and let the patient
 * continue paying it or let it go (frees the doctor's slot right away instead of in ~21 min).
 */
export const PendingPaymentSheet = () => {
  const isLogin = useUserInfoStore(state => state.isLogin);
  const [pending, setPending] = useState<PendingPayment>();
  const [isOpen, setIsOpen] = useState(false);
  const consultPayment = useConsultPayment();
  const centerPayment = useCenterPayment();
  const removeBook = useRemoveBook();
  const [paying, setPaying] = useState(false);
  // Cancel asks why first — the answers show what keeps patients from paying.
  const [askingReason, setAskingReason] = useState(false);
  const [reason, setReason] = useState<CancelReason>();
  const [chosenMethod, setChosenMethod] = useState<string>();
  const [note, setNote] = useState('');
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    // Back on the factor page the gateway trip started from, within the hold time, with no receipt
    // seen in any tab (owner, 2026-10-08: no API call).
    const check = () => {
      const data = readPendingPayment();
      if (!data || !shouldOpenPendingPaymentSheet(data, window.location.pathname)) return;
      // RUM: back from the gateway without paying — this page's events carry labels.book_id.
      setRumBook(data.bookId);
      setPending(data);
      setIsOpen(true);
      track('shown', data);
    };
    check();
    const onPageShow = (event: PageTransitionEvent) => event.persisted && check();
    // Paid in another tab (receipt opened there): close this tab's sheet right away.
    const onStorage = (event: StorageEvent) => {
      if (event.key !== PAID_BOOKS_STORAGE_KEY || readPendingPayment()) return;
      setIsOpen(false);
      setPending(undefined);
      window.dispatchEvent(new Event(PENDING_PAYMENT_CHANGED_EVENT));
    };
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // /factor/v2/<slug>/<service>/ — the factor page's own doctor data (cached when coming from it).
  const factorSlug = pending?.factorPath.startsWith('/factor/v2/') ? decodeURIComponent(pending.factorPath.split('/')[3] ?? '') : '';
  const factorDoctor = useFactorDoctorData(isOpen ? factorSlug : '');
  const factorExpertises = factorDoctor.expertises.data;
  const bookQuery = useBookDetailsQuery({ bookId: pending?.bookId, enabled: isOpen && !!pending && isLogin });
  const book = bookQuery.data?.data?.result?.[0];

  // The slot was already released: nothing to continue.
  useEffect(() => {
    if (!book) return;
    if (Number(book.book_delete) === 1) finish();
  }, [book]);

  const finish = () => {
    clearPendingPayment();
    setIsOpen(false);
    window.dispatchEvent(new Event(PENDING_PAYMENT_CHANGED_EVENT));
  };

  if (!pending) return null;

  const close = () => {
    track('dismissed', pending);
    setIsOpen(false);
  };

  const pay = async (chosenMethod?: string, note?: string) => {
    track('continue', pending, { reason, note, chosen_method: chosenMethod });
    setPaying(true);
    try {
      const payment = pending.centerId === CENTERS.CONSULT ? consultPayment : centerPayment;
      const paymentMethod = chosenMethod ?? (await resolveRetryPaymentMethod(pending.paymentMethod, pending.centerId, pending.priceToman ? pending.priceToman * 10 : undefined));
      const { data } = await payment.mutateAsync({ book_id: pending.bookId, ...(paymentMethod && { payment_method: paymentMethod }) } as any);
      if (data?.status && data?.url) {
        location.assign(data.url);
        return;
      }
      toast.error(data?.message ?? 'پرداخت این نوبت دیگر ممکن نیست. دوباره نوبت بگیرید.');
      finish();
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? 'خطا در آماده‌سازی پرداخت. لطفاً دوباره تلاش کنید.');
    }
    setPaying(false);
  };

  const cancel = (note?: string, then?: () => void) => {
    track('cancel', pending, { reason, note });
    // Leaving for another page: don't close the sheet — closing it pops its history entry (Back),
    // which cancels the navigation that follows.
    const done = then
      ? () => {
          clearPendingPayment();
          then();
        }
      : finish;
    if (!book) return done();
    removeBook.mutate(
      {
        center_id: book.center_id,
        national_code: book.patient?.national_code,
        reference_code: book.reference_code,
        book_id: pending.bookId,
      },
      { onSettled: done },
    );
  };

  // Another doctor / in-person booking: free this doctor's slot first, then go.
  const leaveTo = (url: string, target: string) => {
    track('alternative', pending, { reason, target });
    setLeaving(true);
    cancel(undefined, () => location.assign(url));
  };

  // Doctor card from the factor page's own data (no login needed); the book row only fills gaps.
  const factorName = factorDoctor.fullName.data;
  const doctorName =
    [factorName?.name, factorName?.family].filter(Boolean).join(' ') || (book ? [book.doctor_name, book.doctor_family].filter(Boolean).join(' ') : undefined);
  const doctorAvatar = book?.doctor_user_id
    ? picUserImageUrl(String(book.doctor_user_id))
    : getImageUrl(factorDoctor.image.data?.image, publicRuntimeConfig.CDN_BASE_URL) || undefined;
  const doctorLoading = !doctorName;
  // Same line as under the name on the factor page (its doctor card), not the book row's label.
  const factorExpertise = factorExpertises?.[0];
  const expertise = factorExpertise
    ? getDisplayDoctorExpertise({
        aliasTitle: factorExpertise.alias_title ?? '',
        degree: factorExpertise.degree?.name ?? '',
        expertise: factorExpertise.expertise?.name ?? '',
      })
    : book?.doctor?.display_expertise;
  const bookTime = formatBookTime(book?.book_time_string);
  const bookTimeMs = book?.book_time_string ? moment.from(digitsFaToEn(book.book_time_string), 'fa', 'JYYYY/JMM/JDD HH:mm').valueOf() || undefined : undefined;
  const heldUntil = new Date(pending.at + PENDING_PAYMENT_TTL_MS).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });

  return (
    <Modal isOpen={isOpen} onClose={close} name="pending-payment" noHeader>
      {askingReason ? (
        // Drill-down: full-width reason rows (thumb-sized), then the chosen reason's own page.
        <div className="flex flex-col gap-4 pt-2 min-h-[70vh]">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="بازگشت"
              onClick={() => (reason ? setReason(undefined) : setAskingReason(false))}
              className="flex items-center justify-center w-10 h-10 -ms-2 rounded-full text-slate-700 active:bg-slate-100"
            >
              <ChevronIcon dir="right" width="10" height="16" />
            </button>
            <Text fontSize="lg" fontWeight="bold">
              {reason ? CANCEL_REASONS[reason] : 'چرا نمی‌خواهید ادامه دهید؟'}
            </Text>
          </div>
          {leaving ? (
            <LeavingNotice text="در حال انتقال…" />
          ) : reason ? (
            <PendingPaymentFollowUp
              reason={reason}
              slug={factorSlug}
              serviceId={pending.factorPath.startsWith('/factor/v2/') ? pending.factorPath.split('/')[4] : undefined}
              expertises={factorExpertises}
              doctorName={doctorName}
              bookTime={bookTime}
              priceToman={pending.priceToman}
              turnAt={bookTimeMs}
              previousPaymentMethod={pending.paymentMethod}
              paymentMethod={chosenMethod}
              onPaymentMethodChange={setChosenMethod}
              note={note}
              onNoteChange={setNote}
              onLeaveTo={leaveTo}
              busy={paying || removeBook.isLoading}
              onContinue={method => pay(method, note || undefined)}
              onCancel={() => cancel(note || undefined)}
              onOffer={offer => track('offer', pending, { reason, slug: factorSlug, ...offer })}
            />
          ) : (
            <div className="flex flex-col divide-y divide-slate-100 rounded-2xl border border-slate-200 overflow-hidden">
              {(Object.keys(CANCEL_REASONS) as CancelReason[]).map(key => {
                const Icon = REASON_ICONS[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setReason(key);
                      track('reason', pending, { reason: key });
                    }}
                    className="flex items-center gap-3 w-full min-h-[56px] px-4 text-right bg-white active:bg-slate-50"
                  >
                    <Icon className="w-6 h-6 shrink-0 text-primary" />
                    <Text className="flex-1" fontWeight="medium">
                      {CANCEL_REASONS[key]}
                    </Text>
                    <ChevronIcon dir="left" className="shrink-0 text-slate-700" width="8" height="12" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
      <div className="flex flex-col gap-5 pt-2">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-50">
            <ErrorIcon className="w-7 h-7 text-red-500" />
          </div>
          <Text fontSize="lg" fontWeight="bold">
            پرداخت انجام نشد
          </Text>
        </div>
        <div className="flex flex-col gap-3 p-4 rounded-xl bg-slate-50">
          <div className="flex items-center gap-3">
            {!doctorLoading ? (
              <Avatar src={doctorAvatar} name={doctorName} width={52} height={52} />
            ) : (
              <Skeleton w="52px" h="52px" rounded="full" />
            )}
            <div className="flex flex-col gap-1 min-w-0">
              {!doctorLoading ? (
                <>
                  <Text fontWeight="bold" className="line-clamp-1">
                    {doctorName}
                  </Text>
                  {expertise && (
                    <Text fontSize="sm" className="text-slate-500 line-clamp-1">
                      {expertise}
                    </Text>
                  )}
                </>
              ) : (
                <>
                  <Skeleton w="8rem" h="1rem" rounded="md" />
                  <Skeleton w="6rem" h="0.8rem" rounded="md" />
                </>
              )}
            </div>
          </div>
          {bookTime && (
            <div className="flex items-center gap-2">
              <ChatIcon className="w-5 h-5 shrink-0 text-primary" />
              <Text fontSize="sm">
                پاسخ پزشک:{' '}
                <Text fontSize="sm" fontWeight="semiBold">
                  {bookTime}
                </Text>
              </Text>
            </div>
          )}
          <div className="flex items-center gap-2">
            <SecuritySafeIcon className="w-5 h-5 shrink-0 text-primary" />
            <Text fontSize="sm">
              نوبتتان تا ساعت{' '}
              <Text fontSize="sm" fontWeight="semiBold">
                {heldUntil}
              </Text>{' '}
              رزرو است.
            </Text>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button block onClick={() => pay()} loading={paying}>
            ادامهٔ پرداخت
          </Button>
          <Button block variant="text" className="!text-slate-500" onClick={() => {
              setReason(undefined);
              setAskingReason(true);
            }} disabled={paying}>
            انصراف از نوبت
          </Button>
        </div>
      </div>
      )}
    </Modal>
  );
};

export default PendingPaymentSheet;
